import React from "react";
import {
  EmptyState,
  EmptyStateProps,
  LoadPhase,
  RowActionsMenu,
  SkeletonCard,
  TABLE_BODY_SKELETON,
} from "../../../components/ui";
import type { SubscriptionGate } from "../../billing/hooks/useSubscriptionGate";
import { MACRO_COLORS } from "../macroColors";
import {
  PLAN_STATUS_LABELS,
  PLAN_STATUS_STYLES,
  formatMacro,
} from "../planMapping";
import type { PlanRow } from "../types/plans.types";

/** Each macro column is keyed by a colour chip in its header. */
const MACRO_COLUMNS: Array<{ key: keyof PlanRow["macros"]; label: string }> = [
  { key: "calories", label: "Calories" },
  { key: "protein", label: "Protein" },
  { key: "carbs", label: "Carbs" },
  { key: "fat", label: "Fat" },
];

// Sl, client, type, created on, the macros, status, and the actions.
const COLUMN_COUNT = 6 + MACRO_COLUMNS.length;

const headCell =
  "px-3 sm:px-4 py-3 text-[13px] font-normal text-ink-muted bg-white shadow-[inset_0_-1px_0_#E9EDF1]";
const bodyCell = "px-3 sm:px-4 py-4 text-[14px] text-ink";

export interface PlansTableProps {
  plans: PlanRow[];
  /** Stands in for the rows when the list comes back with none. */
  empty: EmptyStateProps;
  /**
   * Where the page of plans is. The body says nothing while it is being
   * fetched, and only a wait long enough to notice shimmers, as one block the
   * size of the table. Rows already in hand are left alone either way: a plan
   * made in this sitting is on the list before the server has been asked, and
   * it must not blink out while it is.
   */
  phase?: LoadPhase;
  onSelectPlan: (plan: PlanRow) => void;
  onDeletePlan: (plan: PlanRow) => void;
  /** Offered on drafts only. Left out where activating is not on offer. */
  onActivatePlan?: (plan: PlanRow) => void;
  /**
   * How many rows came before this page, so the Sl column keeps counting
   * across pages instead of restarting at 01 on each.
   */
  startIndex?: number;
  /**
   * What this workspace may still do with a plan — see `useSubscriptionGate`.
   *
   * The whole gate rather than one set of props, because this table offers
   * three different acts and each deserves its own wording: opening a plan,
   * sending one, and deleting one.
   *
   * With no subscription behind it a row does not open at all: it stops looking
   * clickable and answers the press with the reason instead. The list itself
   * stays on screen and stays legible — what was built is still theirs, and the
   * row still says who it is for, what it comes to and where it got to.
   */
  gate?: SubscriptionGate;
}

/** An open door, for the callers that have no reason to shut one. */
const OPEN: SubscriptionGate = {
  locked: false,
  props: () => ({ disabled: false }),
};

/**
 * The meal plan list. Kept out of the page because the client screen's Plans
 * tab shows the same table for one client — it takes rows and says nothing
 * about where they came from.
 */
const PlansTable: React.FC<PlansTableProps> = ({
  plans,
  empty,
  phase = "ready",
  onSelectPlan,
  onDeletePlan,
  onActivatePlan,
  startIndex = 0,
  gate = OPEN,
}) => {
  // Worked out once rather than per row: every row in the table wears the same
  // three answers, and none of these three controls is a `Button` — the row and
  // the name take the props apart themselves, and the menu has its own way of
  // going quiet.
  const openGate = gate.props("open a plan");
  const sendGate = gate.props("send a plan");
  const deleteGate = gate.props("delete a plan");

  /**
   * Opens the plan, or says why it will not open — the one place the three ways
   * into the builder agree, since a row, its client's name and the menu's Open
   * plan are all the same act reached three ways.
   */
  const open = (plan: PlanRow) =>
    openGate.disabled ? openGate.onDisabledClick?.() : onSelectPlan(plan);

  return phase !== "ready" && plans.length === 0 ? (
    <div className="flex-1 min-h-0 p-4 sm:p-6">
      <SkeletonCard phase={phase} className={TABLE_BODY_SKELETON} />
    </div>
  ) : (
  // The only scroll region on the page: rows move, the sticky header row and
  // the card's pagination footer stay put.
  <div className="flex-1 min-h-0 overflow-auto custom-scrollbar-overlay">
    {/* Full height only when the rows are gone — see `ClientsTable`. */}
    <table
      className={`w-full table-fixed border-collapse text-left ${
        // The width the columns need is only worth insisting on when there
        // are rows to lay out in them. With none, the min-width would push
        // the empty state out past a phone’s edge and leave the user
        // scrolling sideways to read why the list is empty — so the table
        // takes the scroll region as it finds it and the state centres in it.
        plans.length === 0 ? "h-full" : "min-w-[900px]"
      }`}
    >
      <colgroup>
        <col className="w-[5%]" />
        <col className="w-[22%]" />
        <col className="w-[15%]" />
        <col className="w-[13%]" />
        <col className="w-[8%]" />
        <col className="w-[8%]" />
        <col className="w-[8%]" />
        <col className="w-[8%]" />
        <col className="w-[8%]" />
        <col className="w-[5%]" />
      </colgroup>
      {/* The header pins to the top of this container. Its bottom rule is an
          inset shadow rather than a border because collapsed table borders
          scroll away from a sticky cell. */}
      <thead className="sticky top-0 z-10">
        <tr>
          <th scope="col" className={headCell}>
            Sl
          </th>
          <th scope="col" className={headCell}>
            Client name
          </th>
          <th scope="col" className={headCell}>
            Plan type
          </th>
          <th scope="col" className={headCell}>
            Created on
          </th>
          {MACRO_COLUMNS.map((macro) => (
            <th key={macro.key} scope="col" className={headCell}>
              <span className="inline-flex items-center gap-1.5">
                <span
                  aria-hidden="true"
                  className="h-3 w-[3px] rounded-full"
                  style={{ backgroundColor: MACRO_COLORS[macro.key] }}
                />
                {macro.label}
              </span>
            </th>
          ))}
          <th scope="col" className={headCell}>
            Status
          </th>
          <th scope="col" className={headCell}>
            <span className="sr-only">Actions</span>
          </th>
        </tr>
      </thead>
      {plans.length === 0 ? (
        <tbody>
          <tr>
            <td colSpan={COLUMN_COUNT} className="align-middle">
              <EmptyState {...empty} />
            </td>
          </tr>
        </tbody>
      ) : (
        <tbody>
          {plans.map((plan, index) => (
            <tr
              key={plan.id}
              onClick={() => open(plan)}
              // The pointer and the hover tint are the row's whole claim to
              // being clickable, so both go while it is not. `aria-disabled`
              // has no business on a `tr` — the row is not a control, it is a
              // convenience over the name button inside it, which carries the
              // answer properly.
              title={openGate.title}
              className={`border-b border-line last:border-0 transition-colors ${
                openGate.disabled ? "" : "cursor-pointer hover:bg-[#F8FEFF]"
              }`}
            >
              <td className={`${bodyCell} text-ink-muted`}>
                {String(startIndex + index + 1).padStart(2, "0")}
              </td>
              <td className={bodyCell}>
                {/* The whole row is clickable for pointers, but the name is a
                    real button so the list stays reachable by keyboard. */}
                <button
                  type="button"
                  aria-disabled={openGate.disabled || undefined}
                  title={openGate.title}
                  onClick={(event) => {
                    event.stopPropagation();
                    open(plan);
                  }}
                  // Not dimmed: this is the client's name, and greying it would
                  // read as the client being off rather than the link. The
                  // underline on hover is what offered the plan, so that is
                  // what goes.
                  className={`max-w-full truncate text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/40 rounded ${
                    openGate.disabled ? "cursor-not-allowed" : "hover:underline"
                  }`}
                >
                  {plan.clientName}
                </button>
              </td>
              <td className={bodyCell}>{plan.planType}</td>
              <td className={bodyCell}>{plan.createdOn}</td>
              {MACRO_COLUMNS.map((macro) => (
                <td key={macro.key} className={bodyCell}>
                  {formatMacro(plan.macros[macro.key])}
                </td>
              ))}
              <td className={bodyCell}>
                <span
                  className={`inline-flex whitespace-nowrap rounded-full px-2.5 py-1 text-[12px] font-medium ${
                    PLAN_STATUS_STYLES[plan.status]
                  }`}
                >
                  {PLAN_STATUS_LABELS[plan.status]}
                </span>
              </td>
              <td className={bodyCell}>
                <RowActionsMenu
                  label={`Actions for ${plan.clientName}'s plan`}
                  // Each entry greys rather than leaving, so the press that
                  // comes anyway is answered — a menu that had gone from three
                  // entries to none would read as a plan something is wrong
                  // with rather than as a workspace that has lapsed.
                  actions={[
                    {
                      label: "Open plan",
                      disabled: openGate.disabled,
                      onSelect: () => open(plan),
                    },
                    // Only a draft can be sent: one already with the client
                    // has nowhere to go, so the row does not offer it.
                    ...(plan.status === "draft" && onActivatePlan
                      ? [
                          {
                            label: "Activate plan",
                            disabled: sendGate.disabled,
                            onSelect: sendGate.disabled
                              ? sendGate.onDisabledClick ?? (() => {})
                              : () => onActivatePlan(plan),
                          },
                        ]
                      : []),
                    {
                      label: "Delete plan",
                      destructive: true,
                      disabled: deleteGate.disabled,
                      onSelect: deleteGate.disabled
                        ? deleteGate.onDisabledClick ?? (() => {})
                        : () => onDeletePlan(plan),
                    },
                  ]}
                />
              </td>
            </tr>
          ))}
        </tbody>
      )}
    </table>
  </div>
  );
};

export default PlansTable;
